/**
 * A class's exams — a table; adding or editing one opens its own page.
 *
 * Nothing is edited in place HERE. A row in this list is a row: changing one
 * goes to /exams/:examId, which owns the whole exam, papers included. In-place
 * fields inside a list read as part of the data and leave "have I saved this?"
 * unanswerable.
 *
 * It is a page rather than a dialog because the boards are twelve papers across
 * a fortnight — a modal that size is a page wearing a costume, and it cannot be
 * linked to or reloaded.
 *
 * An exam is a NAME over one or more papers — the boards are twelve, a
 * pre-board five on five dates, a unit test exactly one — and the PAPER carries
 * the date and the scope. So the dialog edits papers, and the table reports the
 * span rather than pretending an exam has a single date.
 *
 * **An exam also sits at a level.** The picker chooses whether this list is the whole
 * class's or one section's; a section's list includes the class's exams, marked as such
 * and not editable from there — the boards are one record every section reads, and
 * correcting a date belongs where every section sees the correction.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, DataTable, SearchFilterBar, Select,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import {
  classSharedApi, schoolsApi, type SchoolClass,
} from '../../api/schoolsApi';
import { EXAM_TYPE_LABELS, type ClassExam } from '@shared-ui/class-shared';

function fmtWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** The SPAN, not one date: an exam with twelve papers runs over a fortnight. */
function span(e: ClassExam): string {
  const ds = e.papers.map(p => p.date).filter(Boolean).sort();
  if (ds.length === 0) return '—';
  return ds[0] === ds[ds.length - 1] ? ds[0] : `${ds[0]} → ${ds[ds.length - 1]}`;
}

/** An exam row, plus whether it is this level's own or read from the class above. */
type ExamRow = ClassExam & { inherited: boolean };

export function ExamsTab({ cls }: { cls: SchoolClass }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sectionId, setSectionId] = useState('');
  const examPath = (examId: string, owningSectionId?: string) =>
    `/schools/${cls.schoolId}/classes/${cls.id}/exams/${examId}`
    + (owningSectionId ? `?section=${encodeURIComponent(owningSectionId)}` : '');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState | undefined>({ sortBy: 'span', sortOrder: 'asc' });
  const subjectLabel = useSubjectLabel();

  const { data: sectionData } = useQuery({
    queryKey: ['class-sections', cls.id],
    queryFn: () => schoolsApi.listSections(cls.id),
  });
  const sections = useMemo(() => sectionData?.sections ?? [], [sectionData]);
  const owner = useMemo(
    () => ({ schoolClassId: cls.id, sectionId: sectionId || undefined }),
    [cls.id, sectionId],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['class-exams', cls.id, sectionId],
    queryFn: () => classSharedApi.listExams(owner),
  });
  // The class's own, read alongside a section's rather than copied into it — inheritance
  // happens at read time, which is why one wrong board date is one edit and not three.
  const { data: fromClass } = useQuery({
    queryKey: ['class-exams', cls.id, ''],
    queryFn: () => classSharedApi.listExams({ schoolClassId: cls.id }),
    enabled: !!sectionId,
  });

  const all = useMemo<ExamRow[]>(() => [
    ...(data?.exams ?? []).map(e => ({ ...e, inherited: false })),
    ...(sectionId ? (fromClass?.exams ?? []).map(e => ({ ...e, inherited: true })) : []),
  ], [data, fromClass, sectionId]);

  const remove = useMutation({
    mutationFn: (examId: string) => classSharedApi.deleteExam(owner, examId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['class-exams', cls.id] }),
  });

  const filterDefs = useMemo<FilterDef[]>(() => [{
    key: 'type',
    placeholder: 'All types',
    // From the data: a filter offering a type this class has never sat is a
    // dead end.
    options: [...new Set(all.map(e => e.type))].sort()
      .map(v => ({ value: v, label: EXAM_TYPE_LABELS[v] ?? v })),
  }], [all]);

  const rows = useMemo(() => {
    const term = (filters.search ?? '').trim().toLowerCase();
    return all.filter(e => {
      if (filters.type && e.type !== filters.type) return false;
      if (!term) return true;
      // Searchable by what is SHOWN as well as by the key, so typing the
      // display name finds it.
      return [e.name, ...e.papers.flatMap(p =>
        [p.label ?? '', ...p.subjects, ...p.subjects.map(subjectLabel)])]
        .some(v => v.toLowerCase().includes(term));
    });
  }, [all, filters]);

  const columns: ColumnDef<ExamRow>[] = [
    { key: 'name', header: 'Exam', sortable: true, sortValue: e => e.name.toLowerCase(), render: e => e.name },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      sortValue: e => e.type,
      render: e => EXAM_TYPE_LABELS[e.type] ?? e.type,
    },
    { key: 'papers', header: 'Papers', sortable: true, sortValue: e => e.papers.length, render: e => String(e.papers.length) },
    {
      key: 'span',
      header: 'Dates',
      sortable: true,
      sortValue: e => e.papers.map(p => p.date).sort()[0] ?? '',
      render: e => span(e),
    },
    {
      key: 'subjects',
      header: 'Subjects',
      // Across all its papers — a pre-board covers the lot. Bounded and wrapped:
      // ten subject names on one line pushed every column after it off screen.
      width: '30%',
      className: 'exams-subjects',
      render: e => [...new Set(e.papers.flatMap(p => p.subjects))].map(subjectLabel).join(', ') || '—',
    },
    {
      key: 'updatedBy',
      header: 'Modified by',
      sortable: true,
      sortValue: e => (e.updatedByName ?? '').toLowerCase(),
      render: e => e.updatedByName ?? '—',
    },
    {
      key: 'updatedAt',
      header: 'Modified on',
      sortable: true,
      sortValue: e => e.updatedAt ?? e.createdAt ?? '',
      render: e => fmtWhen(e.updatedAt ?? e.createdAt),
    },
    // Only meaningful while looking at one section: at class level every row is the
    // class's, and a column of one repeated value is noise.
    ...(sectionId ? [{
      key: 'level',
      header: 'Sat for',
      sortable: true,
      sortValue: (e: ExamRow) => (e.inherited ? 1 : 0),
      render: (e: ExamRow) => (e.inherited ? 'Whole class' : 'This section'),
    } as ColumnDef<ExamRow>] : []),
    {
      key: 'actions',
      header: '',
      // Both actions are ICONS. A worded button beside a cross reads as two
      // different kinds of control for two things that are the same kind of
      // thing — act on this row.
      render: e => (
        <span className="school-row-actions">
          <button
            type="button"
            className="school-row-icon"
            aria-label={`Edit ${e.name}`}
            title={e.inherited
              ? `Edit ${e.name} — it belongs to the whole class, so the change reaches every section`
              : `Edit ${e.name}`}
            onClick={ev => { ev.stopPropagation(); navigate(examPath(e.id, e.inherited ? undefined : sectionId)); }}
          >✎</button>
          {/* No delete on an inherited row. It is not this section's to remove, and a
              cross here would read as "take it off our timetable" while actually
              deleting it for every section. */}
          {!e.inherited && (
            <button
              type="button"
              className="school-row-icon school-row-icon--danger"
              disabled={remove.isPending}
              aria-label={`Delete ${e.name}`}
              title={`Delete ${e.name} and all its papers — everyone in the class loses it`}
              onClick={ev => { ev.stopPropagation(); remove.mutate(e.id); }}
            >×</button>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="school-section">
      <div className="school-toolbar">
        {sections.length > 0 && (
          <Select
            value={sectionId}
            aria-label="Which level's exams"
            onChange={e => setSectionId(e.target.value)}
          >
            <option value="">Whole class</option>
            {sections.map(x => <option key={x.id} value={x.id}>{x.name} only</option>)}
          </Select>
        )}
        <Button variant="primary" size="sm" onClick={() => navigate(examPath('new', sectionId || undefined))}>
          {sectionId ? '+ Add exam for this section' : '+ Add exam'}
        </Button>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search exam, paper, subject…"
        hideFetch
      />

      <DataTable<ExamRow>
        columns={columns}
        data={rows}
        rowKey={e => e.id}
        loading={isLoading}
        bordered
        sort={sort}
        onSortChange={setSort}
        onRowClick={e => navigate(examPath(e.id, e.inherited ? undefined : sectionId))}
        emptyMessage={all.length === 0
          ? 'No exams recorded for this class yet.'
          : 'No exams match those filters.'}
      />

      {remove.isError && <p className="school-error">{(remove.error as Error).message}</p>}

    </section>
  );
}
